import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useWebsiteConfigQuery } from "hooks/useWebsiteConfig";

// ─── Skeleton shimmer ────────────────────────────────────────────────────────
const shimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

const Skeleton = styled.div`
  border-radius: ${({ radius }) => radius || "6px"};
  height: ${({ h }) => h || "14px"};
  width: ${({ w }) => w || "100%"};
  background: linear-gradient(90deg, #2a2e30 25%, #343a3c 50%, #2a2e30 75%);
  background-size: 600px 100%;
  animation: ${shimmer} 1.4s infinite linear;
`;

// ─── Styled components ────────────────────────────────────────────────────────
const Container = styled.div`
  color: #f5f6f7;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  width: 100%;
`;

const PageTitle = styled.h2`
  font-size: 24px;
  font-weight: 800;
  color: #ffffff;
  margin: 0 0 20px 0;
`;

const CategoriesContainer = styled.div`
  display: inline-flex;
  background: #122129;
  border-radius: 9999px;
  padding: 4px;
  margin-bottom: 20px;
  overflow-x: auto;
  max-width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.05);
  &::-webkit-scrollbar { display: none; }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const CategoryButton = styled.button`
  padding: 12px 16px;
  border: none;
  background: ${props => props.active ? "rgba(20, 117, 225, 0.15)" : "transparent"};
  color: ${props => props.active ? "#1475e1" : "#b1bad3"};
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  border-bottom: 2px solid ${props => props.active ? "#1475e1" : "transparent"};
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    color: ${props => props.active ? "#1475e1" : "#ffffff"};
  }
`;

const AccordionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const AccordionItem = styled.div`
  background: oklch(0.215 0.025 230);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  overflow: hidden;
`;

const AccordionTrigger = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: transparent;
  border: none;
  color: #ffffff;
  font-weight: 600;
  font-size: 14.5px;
  text-align: left;
  cursor: pointer;
  &:hover { background: rgba(255, 255, 255, 0.02); }
`;

const AccordionContent = styled.div`
  padding: 0 20px 20px 20px;
  font-size: 14px;
  color: #b1bad3;
  line-height: 1.6;
`;

const SkeletonTabsRow = styled.div`
  display: inline-flex;
  gap: 8px;
  background: #122129;
  border-radius: 9999px;
  padding: 4px 8px;
  margin-bottom: 20px;
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const SkeletonAccordionItem = styled.div`
  background: #122129;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// ─── Component ───────────────────────────────────────────────────────────────
const Faq = () => {
  const [category, setCategory] = useState("General");
  const [openIndex, setOpenIndex] = useState(null);
  const { data: config, isLoading } = useWebsiteConfigQuery();

  // Parse FAQs from backend — no static fallback
  const raw = config?.affiliate_faq;
  const allFaqs = raw
    ? (typeof raw === "string" ? JSON.parse(raw) : raw)
    : [];

  // Derive unique category tabs from actual data
  const categories = Array.isArray(allFaqs) && allFaqs.length > 0
    ? [...new Set(allFaqs.map(f => f.category || "General"))]
    : ["General", "Affiliate Program", "Earnings"];

  const categoryFaqs = Array.isArray(allFaqs)
    ? allFaqs.filter(f => (f.category || "General") === category)
    : [];

  return (
    <div style={{ color: "#f5f6f7", fontFamily: "inherit", width: "100%" }}>
      <h2 style={{ fontSize: "24px", fontWeight: "800", color: "#ffffff", margin: "0 0 20px 0" }}>FAQ</h2>

      {/* ── Category tabs ── */}
      {isLoading ? (
        <div style={{ display: "inline-flex", gap: "8px", background: "#122129", borderRadius: "9999px", padding: "4px 8px", marginBottom: "20px", border: "1px solid rgba(255, 255, 255, 0.05)" }}>
          <Skeleton w="80px" h="34px" radius="9999px" />
          <Skeleton w="130px" h="34px" radius="9999px" />
          <Skeleton w="90px" h="34px" radius="9999px" />
        </div>
      ) : (
        <div style={{ display: "inline-flex", gap: "6px", background: "#122129", borderRadius: "9999px", padding: "4px", marginBottom: "20px", border: "1px solid rgba(255, 255, 255, 0.05)", overflowX: "auto", maxWidth: "100%" }}>
          {categories.map(c => {
            const isActive = category === c;
            return (
              <button
                key={c}
                onClick={() => { setCategory(c); setOpenIndex(null); }}
                style={{
                  padding: "8px 20px",
                  border: "none",
                  borderRadius: "9999px",
                  background: isActive ? "#213743" : "transparent",
                  color: isActive ? "#ffffff" : "#b1bad3",
                  fontWeight: "700",
                  fontSize: "13.5px",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  whiteSpace: "nowrap"
                }}
              >
                {c}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Accordion list ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%" }}>
        {isLoading
          ? [0, 1, 2, 3].map(i => (
              <div key={i} style={{ background: "#122129", border: "1px solid rgba(255, 255, 255, 0.05)", borderRadius: "6px", padding: "16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Skeleton w={`${55 + i * 8}%`} h="14px" />
                <Skeleton w="18px" h="18px" radius="4px" style={{ flexShrink: 0, marginLeft: 12 }} />
              </div>
            ))
          : categoryFaqs.length === 0
          ? (
              <div style={{ background: "oklch(0.215 0.025 230)", border: "1px solid rgba(255, 255, 255, 0.05)", borderRadius: "8px", overflow: "hidden" }}>
                <div style={{ cursor: "default", color: "#b1b6bb", padding: "16px 20px", fontStyle: "italic" }}>
                  No FAQs available for this category yet.
                </div>
              </div>
            )
          : categoryFaqs.map((faq, i) => {
              const isOpen = openIndex === i;
              return (
                <div key={i} style={{ background: "oklch(0.215 0.025 230)", border: "1px solid rgba(255, 255, 255, 0.05)", borderRadius: "8px", overflow: "hidden" }}>
                  <button 
                    onClick={() => setOpenIndex(isOpen ? null : i)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "16px 20px",
                      background: "transparent",
                      border: "none",
                      color: "#ffffff",
                      fontWeight: "600",
                      fontSize: "14.5px",
                      textAlign: "left",
                      cursor: "pointer",
                      outline: "none"
                    }}
                  >
                    <span>{faq.question}</span>
                    {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                  {isOpen && (
                    <div style={{ padding: "0 20px 20px 20px", fontSize: "14px", color: "#b1bad3", lineHeight: "1.6" }}>
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })
        }
      </div>
    </div>
  );
};

export default Faq;
